import { ConsommationFlotte, ConsommationFlotteList } from '@services/interfaces/generated/YSER-v1';
import { TransformBaseType, transformUnit } from '@shared/misc/helpers/data';
import { useCallback, useMemo } from 'react';
import { ConsommationFlotteData, UseBilanCarboneGraphProps } from './type';

const useData = (data: ConsommationFlotteList | undefined): UseBilanCarboneGraphProps => {
  const selectedUnitCO2 = useMemo<TransformBaseType>(() => {
    const tabNumbersCo2 = data?.items.map((item) => item.co2eq) ?? [];
    const valeurCo2 = Math.max(...tabNumbersCo2);
    const tempUnit = transformUnit(valeurCo2, 'co2eq');
    return tempUnit;
  }, [data?.items]);

  const selectedUnitData = useMemo<TransformBaseType>(() => {
    const tabNumbersData = data?.items.map((item) => item.data) ?? [];
    const valeurData = Math.max(...tabNumbersData);
    const tempUnit = transformUnit(valeurData, 'data');
    return tempUnit;
  }, [data?.items]);

  const consoData = useMemo<ConsommationFlotteData[] | undefined>(() => {
    // Générer les 12 derniers mois glissants
    const now = new Date();
    const last12Months: { dateKey: string; timestamp: number }[] = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = date.toLocaleString('default', { month: 'short' }).replace('.', '');
      const year = date.getFullYear() % 100;
      const dateKey = `${month.charAt(0).toUpperCase() + month.slice(1)}.${year}`;
      last12Months.push({ dateKey, timestamp: date.getTime() });
    }

    // Grouper les données existantes par mois
    const groupedByDate: Record<string, number> = {};
    if (data?.items && data.items.length > 0) {
      data.items.forEach((item) => {
        const date = new Date(item.date ?? new Date());
        const month = date.toLocaleString('default', { month: 'short' }).replace('.', '');
        const year = date.getFullYear() % 100;
        const dateKey = `${month.charAt(0).toUpperCase() + month.slice(1)}.${year}`;
        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = 0;
        }
        groupedByDate[dateKey] += item.co2eq ?? 0;
      });
    }

    // Créer le tableau final avec tous les 12 mois
    return last12Months.map(({ dateKey }) => {
      const total = groupedByDate[dateKey] ?? 0;
      const valueWithSelectedUnit = transformUnit(total, 'co2eq', selectedUnitCO2.index);
      return {
        x: dateKey,
        y: +valueWithSelectedUnit.value,
      };
    });
  }, [data?.items, selectedUnitCO2.index]);

  return {
    consoData,
    unitToShow: selectedUnitCO2.unit,
    unitIndexCO2: selectedUnitCO2.index,
    unitIndexData: selectedUnitData.index,
    unitLabelCO2: `Empreinte carbone (${selectedUnitCO2.unit})`,
    unitLabelData: `Consommation data (${selectedUnitData.unit})`,
  };
};

export default useData;
